import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { MarketDivider, MarketHeader, MarketModalFull } from '@market/react';

import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { CartItems } from './components/cart_items';
import { useGetOrderSummary } from 'typescript/hooks/use_order_actions_client';
import { getKioskPricingContext } from 'utilities/software';
import notNullOrUndefined from 'typescript/utilities/not_null_or_undefined';
import { TotalReceiptItem } from 'typescript/apps/checkout_v3/components/order_summary/components/total_receipt_item';

import styles from 'typescript/apps/checkout_v3/components/order_summary/order_summary.module.scss';
import { Subtotals } from 'typescript/apps/checkout_v3/components/order_summary/components/subtotals';
import { Discounts } from 'typescript/apps/checkout_v3/components/order_summary/components/discounts';
import { LoadingState } from 'typescript/apps/checkout_v3/components/loading_state';
import { ErrorState } from 'typescript/apps/checkout_v3/components/order_summary/components/error_state';

const OrderSummary = () => {
  const { orderNumber } = useCheckoutFlow();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (
      i18n.exists('checkout.orderSummary.disclaimer') &&
      notNullOrUndefined(window.Sq.SqShopFooter) &&
      !window.Sq.SqShopFooter.disclaimers.includes(
        t('checkout.orderSummary.disclaimer'),
      )
    ) {
      window.Sq.SqShopFooter.disclaimers = [
        t('checkout.orderSummary.disclaimer'),
        ...window.Sq.SqShopFooter.disclaimers,
      ];
    }
  }, [i18n, t]);

  const { orderSummaryData, isLoading, error } = useGetOrderSummary({
    orderNumber,
  });

  if (isLoading) {
    return (
      <div className={styles.loadingWrapper}>
        <LoadingState indicatorSize="small" />
      </div>
    );
  }

  if (error || !orderSummaryData) {
    return (
      <div className={styles.loadingWrapper}>
        <ErrorState />
      </div>
    );
  }

  const { isPnpSubscribed, locationCount, kioskDeviceCount } =
    getKioskPricingContext(orderSummaryData);

  return (
    <div className={styles.wrapper}>
      <CartItems
        cartSummary={orderSummaryData.cart_summary}
        isPnpSubscribed={isPnpSubscribed}
        locationCount={locationCount}
        kioskDeviceCount={kioskDeviceCount}
      />
      <MarketDivider size="thin" margin="small" />
      <Subtotals orderSummaryData={orderSummaryData} />
      <Discounts orderSummaryData={orderSummaryData} />
      <MarketDivider size="thin" margin="small" />
      {/* TODO: handle JP issuer installments, display number of payments selected by seller */}
      <TotalReceiptItem
        label={t('checkout.receipt.label.v3.todaysTotal')}
        cost={orderSummaryData.payment_due.amount}
        currency={orderSummaryData.payment_due.currency}
        includesShippingAndTax={true}
      />
    </div>
  );
};

type OrderSummaryModalProps = {
  setShowModal?: (showModal: boolean) => void;
};

export const OrderSummaryModal = ({ setShowModal }: OrderSummaryModalProps) => {
  const { t } = useTranslation();

  return (
    <MarketModalFull
      layout="fluid"
      onMarketDialogDismissed={() => setShowModal?.(false)}
    >
      <MarketHeader>
        <h2>{t('checkout.receipt.label.v3.yourCart')}</h2>
      </MarketHeader>
      <div className={styles.modalWrapper}>
        <OrderSummary />
      </div>
    </MarketModalFull>
  );
};

export default OrderSummary;
